import { useMemo } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { useQuery } from '@mint-lib/query-client';
import { useFlags } from '@mint-lib/routing-context';
import { Users } from '@mint-sdk/api-ai-assistant';

import {
  readDocuments,
  readListing,
} from '../../helpers/knowledge-base/fileSystemListing.js';
import { getKnowledgeBaseListingOption } from '../../queries/knowledgeBase.js';
import { getDocumentsOption } from '../../queries/rag.js';
import { ROOT_FOLDER_ID } from '../../types/fileSystem.js';

type Params = {
  folderId: string;
  search?: string;
};

export const useFileSystemNodes = ({ folderId, search = '' }: Params) => {
  const {
    aiFileSystem: { enabled: isFileSystemEnabled },
  } = useFlags('@mint/arm', ['aiFileSystem']);
  const { t } = useTranslation('@mint-lib/ai-chat');

  // aiFileSystem cleanup: delete this query, `fromDocuments` and `readDocuments`.
  const { data: documents = [], isLoading: isLoadingDocuments } = useQuery({
    ...getDocumentsOption(),
    enabled: !isFileSystemEnabled,
  });

  const {
    data: listing,
    isLoading: isLoadingListing,
    isError: isListingError,
    refetch: refetchListing,
  } = useQuery({
    ...getKnowledgeBaseListingOption(folderId),
    enabled: isFileSystemEnabled,
  });

  const { data: rootListing } = useQuery({
    ...getKnowledgeBaseListingOption(ROOT_FOLDER_ID),
    enabled: isFileSystemEnabled,
  });

  const { data: me } = Users.getMe.use({});
  const myUuid = me?.userUuid;

  const query = search.trim().toLowerCase();

  const fromListing = useMemo(
    () => readListing(listing, folderId, query),
    [listing, folderId, query],
  );

  const fromDocuments = useMemo(
    () => readDocuments(documents, query),
    [documents, query],
  );

  const nodes = isFileSystemEnabled ? fromListing : fromDocuments;

  const currentPrivateBucket = useMemo(() => {
    const bucketId = nodes.breadcrumb[0]?.folderId;

    if (!bucketId) return null;

    return (
      readListing(rootListing, ROOT_FOLDER_ID, '').privateBuckets.find(
        ({ id }) => id === bucketId,
      ) ?? null
    );
  }, [rootListing, nodes.breadcrumb]);

  const isInOwnPrivateBucket =
    !!myUuid && currentPrivateBucket?.ownerUuid === myUuid;

  const ownPrivateBucket = useMemo(() => {
    if (!myUuid) return null;

    return (
      nodes.privateBuckets.find(({ ownerUuid }) => ownerUuid === myUuid) ?? null
    );
  }, [myUuid, nodes.privateBuckets]);

  const breadcrumbItems = useMemo(
    () => [
      { folderId: ROOT_FOLDER_ID, name: t('My Files') },
      ...nodes.breadcrumb.map((crumb) =>
        crumb.folderId === currentPrivateBucket?.id
          ? { ...crumb, name: currentPrivateBucket.name }
          : crumb,
      ),
    ],
    [nodes.breadcrumb, currentPrivateBucket, t],
  );

  return {
    ...nodes,
    breadcrumbItems,
    currentPrivateBucket,
    ownPrivateBucket,
    isInOwnPrivateBucket,
    depth: nodes.breadcrumb.length,
    isLoading: isFileSystemEnabled ? isLoadingListing : isLoadingDocuments,
    isError: isFileSystemEnabled && isListingError,
    refetch: refetchListing,
  };
};
